import { CAPABILITIES, type Capability, type FeatureAccessResult } from "@ibada/shared-types/feature-flags"

import { useAccessModel } from "@/features/auth"
import { WorkspaceAccessModule } from "@/features/auth/model/auth-types"

import { useFeatureFlag } from "./use-feature-flag"

/**
 * Resolve whether the current user can use a capability, composing the three independent gates:
 * feature flag (rollout) AND permission (CASL) AND entitlement (billing). Returns the first failing
 * gate as `reason` so the UI can choose the affordance: hide (`flag`), ask-admin (`permission`), or
 * upgrade-CTA (`plan`).
 *
 * Phase 1: only the flag gate is populated; `permission`/`entitlement` are null on every capability, so
 * those branches are inert. They are wired here already so adding a permission/entitlement to a
 * capability definition lights up every call site with no edits.
 *
 * This is UX only. Sensitive features must also be guarded on the server (`requireFeatureAccess`).
 */
export function useFeatureAccess(capability: Capability): FeatureAccessResult {
  const definition = CAPABILITIES[capability]
  const flagEnabled = useFeatureFlag(definition.flag)
  const accessModel = useAccessModel()

  if (!flagEnabled) return { allowed: false, reason: "flag" }

  if (definition.permission) {
    const branchAccess = accessModel.branch(definition.permission.module as WorkspaceAccessModule)
    const permitted = definition.permission.level === "EDIT" ? branchAccess.canEdit : branchAccess.canView
    if (!permitted) return { allowed: false, reason: "permission" }
  }

  if (definition.entitlement) {
    // Entitlement check lands with billing (Phase: subscriptions). Until then no capability is plan-gated.
    return { allowed: false, reason: "plan" }
  }

  return { allowed: true, reason: null }
}
